import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, Eye, X, FileDown, Link2 } from "lucide-react";
import { jsPDF } from "jspdf";
import { api } from "@/lib/api";

const NA = "[NO RESPONDIDO]";
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("es", { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; } };
const empty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
const val = (v) => empty(v) ? NA : (Array.isArray(v) ? v.join(", ") : String(v));

function assessmentText(rec) {
  const d = rec.data || {};
  const priceLine = (l, v) => `  - ${l}: ${empty(v) ? NA : "$" + v}`;
  const others = (d.other_services || []).filter((s) => s.service).map((s) => `  - ${s.service}: ${s.price ? "$" + s.price : NA}`).join("\n");
  return [
    ["EVALUACIÓN DE SEGUIMIENTO — " + (rec.client_name || "").toUpperCase(), [
      `Tipo: ${rec.assessment_type || "Follow-Up Assessment"}`,
      `Fecha de envío: ${fmt(rec.created_at)}`,
      `Conectada al perfil inicial: ${rec.client_id ? `Sí (ID ${rec.client_id})` : "No encontrado en esta base de datos"}`,
    ]],
    ["1. INFORMACIÓN DEL NEGOCIO", [
      `Seguro comercial: ${val(d.business_insurance)}${d.insurance_type ? ` — ${d.insurance_type}` : ""}`,
      `Registro: ${val(d.business_registration)}${d.legal_name ? ` — Nombre legal: ${d.legal_name}` : ""}`,
      `Equipo: ${val(d.team_work)}${d.team_work_other ? ` (${d.team_work_other})` : ""}`,
      `Trabajan sin ella: ${val(d.cleans_with_team)}`,
    ]],
    ["2. PRECIOS Y COTIZACIONES", [
      "Precios actuales:", priceLine("Limpieza regular", d.price_regular), priceLine("Limpieza profunda", d.price_deep),
      priceLine("Mudanza (Move-In/Out)", d.price_move), priceLine("Limpieza comercial", d.price_commercial),
      ...(others ? [others] : []),
      `Notas de precios: ${val(d.pricing_notes)}`,
      `Cómo calcula precios: ${val(d.pricing_method)}${d.pricing_method_other ? ` (${d.pricing_method_other})` : ""}`,
      `Por qué no se siente segura: ${val(d.pricing_confidence)}${d.pricing_confidence_other ? ` (${d.pricing_confidence_other})` : ""}`,
      `Cómo se siente con sus precios: ${val(d.price_feeling)}`,
      `Reacción de clientes: ${val(d.client_reactions)}${d.client_reactions_other ? ` (${d.client_reactions_other})` : ""}`,
      `Experiencia que le hizo dudar: ${val(d.pricing_doubt_story)}`,
    ]],
    ["3. CLIENTES Y REFERIDOS", [
      `Clientes recurrentes: ${val(d.recurring_clients)}${!empty(d.recurring_frequency) ? ` — Frecuencia: ${d.recurring_frequency.join(", ")}` : ""}`,
      `Cantidad de recurrentes: ${val(d.recurring_count)}`,
      `La recomiendan: ${val(d.referrals)}`,
      `Programa de referidos: ${val(d.referral_program)}${d.referral_offer ? ` — Ofrece: ${d.referral_offer}` : ""}${d.referral_interest ? ` — Interés: ${d.referral_interest}` : ""}`,
    ]],
    ["4. CÓMO ENCUENTRA CLIENTES", [
      `Métodos: ${val(d.find_clients)}${d.find_clients_other ? ` (${d.find_clients_other})` : ""}`,
      `Páginas de negocio: ${val(d.social_pages)}${d.social_links ? ` — ${d.social_links}` : ""}`,
      `Google Business: ${val(d.google_business)}`,
    ]],
    ["5. NETWORKING Y ALIANZAS", [
      `Hace networking: ${val(d.networking)}${d.networking_connections ? ` — ${d.networking_connections}` : ""}`,
      `Le interesa conectar con: ${val(d.networking_interests)}${d.networking_interests_other ? ` (${d.networking_interests_other})` : ""}`,
    ]],
    ["6. LIMPIEZA COMERCIAL", [
      `Ha contactado empresas: ${val(d.contacted_business)}${d.contacted_details ? ` — ${d.contacted_details}` : ""}`,
      `Clientes comerciales deseados: ${val(d.commercial_types)}${d.commercial_types_other ? ` (${d.commercial_types_other})` : ""}`,
      `Ha hecho limpieza comercial: ${val(d.done_commercial)}${d.commercial_experience ? ` — ${d.commercial_experience}` : ""}`,
    ]],
    ["7. NUEVOS SERVICIOS", [
      `Limpieza de botes de basura: ${val(d.trash_idea)}`,
      `Cómo ofrecerlo: ${val(d.trash_offer)}`,
      `Limpieza de carros: ${val(d.car_cleaning)}${d.car_details ? ` — ${d.car_details}` : ""}${d.car_other_service ? ` — Se refería a: ${d.car_other_service}` : ""}`,
      `Otros servicios futuros: ${val(d.other_services_future)}`,
    ]],
    ["8. CRECIMIENTO DEL NEGOCIO", [
      `Servicios que más disfruta: ${val(d.most_enjoy)}`,
      `Servicios que más dinero generan: ${val(d.most_profitable)}`,
      `Servicio que dejaría: ${val(d.drop_service)}${d.drop_service_detail ? ` — ${d.drop_service_detail}` : ""}`,
    ]],
    ["9. SU VISIÓN (12 MESES)", [
      d.vision_12mo || NA,
      `Mayor obstáculo: ${val(d.biggest_obstacle)}${d.biggest_obstacle_other ? ` (${d.biggest_obstacle_other})` : ""}`,
      `Detalle: ${val(d.obstacle_detail)}`,
    ]],
    ["10. ÁREAS DE AYUDA SOLICITADAS", [
      val(d.help_areas) + (d.help_areas_other ? ` (${d.help_areas_other})` : ""),
    ]],
  ];
}

const Sec = ({ t, children }) => <div className="mb-4 bg-offwhite rounded-2xl p-4 border border-navy/5"><h4 className="font-heading font-bold text-navy mb-2">{t}</h4>{children}</div>;
const Row = ({ l, v }) => <div className="mb-1.5 text-sm"><span className="font-semibold text-navy">{l}: </span><span className="text-ink/70 whitespace-pre-wrap">{v}</span></div>;

function DetailModal({ rec, onClose, onPatch }) {
  const [notes, setNotes] = useState(rec.admin_notes || "");
  const sections = assessmentText(rec);

  const downloadPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 40; const width = doc.internal.pageSize.getWidth() - margin * 2;
    let y = margin;
    const addLines = (text, size, bold, color) => {
      doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); doc.setTextColor(color || "#172033");
      doc.splitTextToSize(text, width).forEach((ln) => {
        if (y > doc.internal.pageSize.getHeight() - margin) { doc.addPage(); y = margin; }
        doc.text(ln, margin, y); y += size + 4;
      });
    };
    sections.forEach(([t, lines], idx) => { y += idx ? 8 : 0; addLines(t, idx ? 12 : 16, true, idx ? "#7A3E9D" : "#123B6D"); addLines(lines.join("\n"), 10, false); });
    doc.save(`evaluacion_seguimiento_${(rec.client_name || "cliente").replace(/[^a-z0-9]+/gi, "_").toLowerCase()}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="fu-detail-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-navy text-white px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div><h3 className="font-heading font-extrabold text-lg">Evaluación de Seguimiento — {rec.client_name}</h3><p className="text-white/60 text-sm">{fmt(rec.created_at)}</p></div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="fu-detail-close"><X size={22} /></button>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-3 mb-5 items-center">
            <button onClick={downloadPDF} data-testid="fu-download-pdf" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold bg-gold text-navy"><FileDown size={15} /> Imprimir / Guardar como PDF</button>
            {rec.client_id && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand bg-aqua rounded-full px-3 py-1.5" data-testid="fu-linked"><Link2 size={13} /> Conectada al perfil inicial</span>}
          </div>
          {sections.map(([t, lines]) => (
            <Sec key={t} t={t}>{lines.map((l, i) => <Row key={i} l="" v={l} />)}</Sec>
          ))}
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Notas administrativas (privadas)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => onPatch(rec.id, { admin_notes: notes })} rows={3} className="w-full bg-white border border-navy/15 rounded-xl px-3 py-2 text-sm" data-testid="fu-admin-notes" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FollowupsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => { setLoading(true); try { const { data } = await api.get("/followup-assessments"); setItems(data); } catch (e) { /* ignore */ } setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, changes) => {
    setItems((p) => p.map((r) => r.id === id ? { ...r, ...changes } : r));
    setOpen((o) => (o && o.id === id ? { ...o, ...changes } : o));
    try { await api.patch(`/followup-assessments/${id}`, changes); } catch (e) { load(); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-ink/60 text-sm"><span className="font-bold text-navy" data-testid="fu-count">{items.length}</span> evaluación(es) de seguimiento</p>
        <button onClick={load} className="inline-flex items-center gap-2 bg-white border border-navy/10 rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-aqua"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
      </div>
      {items.length === 0 ? <div className="text-center py-20 bg-white rounded-3xl border text-ink/50">No hay evaluaciones de seguimiento todavía.</div> :
        <div className="bg-white rounded-3xl border border-navy/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fu-table">
              <thead className="bg-offwhite text-navy"><tr>{["Fecha", "Cliente", "Tipo", "Perfil", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-navy/5 hover:bg-aqua/20" data-testid={`fu-row-${r.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-ink/60">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-navy">{r.client_name}</td>
                    <td className="px-4 py-3 text-ink/70">{r.assessment_type}</td>
                    <td className="px-4 py-3">{r.client_id ? <span className="text-xs bg-aqua text-navy rounded-full px-2.5 py-1 font-semibold">Conectado</span> : <span className="text-xs bg-slate-100 text-ink/50 rounded-full px-2.5 py-1">—</span>}</td>
                    <td className="px-4 py-3"><button onClick={() => setOpen(r)} data-testid={`fu-view-${r.id}`} className="inline-flex items-center gap-1 text-brand font-semibold whitespace-nowrap"><Eye size={15} /> Ver</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>}
      {open && <DetailModal rec={open} onClose={() => setOpen(null)} onPatch={patch} />}
    </div>
  );
}
